import { api } from '@agency/shared';
import { Button, Callout, Card, Checkbox, Chip, Field, Input, KeyValue, PageHeader, Select, Skeleton, Textarea, fmtTimestamp } from '@agency/ui';
import { useEffect, useState } from 'react';
import { ErrorCallout, Grid, Page, SensitiveButton, UserLink } from '../../app/widgets.js';
import { ROLLOUT_LABEL, ROLLOUT_TONE } from '../../lib/labels.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi } from '../../lib/useApi.js';

export function ReleasePolicyPage() {
  const session = useSession();
  const mutation = useMutation();
  const policyQ = useApi('admin.ops.releasePolicy.get');
  const people = useApi('admin.people.list', { input: { limit: 200, status: 'active' } }, { enabled: session.has(['people.manage', 'audit.read', 'account.assign']) });
  const policy = policyQ.data?.policy;
  const canEdit = session.has('device.manage');

  const [minAppVersion, setMinAppVersion] = useState('');
  const [currentAppVersion, setCurrentAppVersion] = useState('');
  const [channel, setChannel] = useState<api.ReleaseChannel>('stable');
  const [stage, setStage] = useState<api.RolloutStage>('cohort_3');
  const [approved, setApproved] = useState('');
  const [stageBuildIds, setStageBuildIds] = useState<Record<string, string>>({});
  const [cohorts, setCohorts] = useState<api.Cohort[]>([]);
  useEffect(() => {
    if (!policy) return;
    setMinAppVersion(policy.minAppVersion);
    setCurrentAppVersion(policy.currentAppVersion);
    setChannel(policy.channel);
    setStage(policy.stage);
    setApproved(policy.approvedBuildIds.join('\n'));
    setStageBuildIds(Object.fromEntries(Object.entries(policy.stageBuildIds).map(([k, v]) => [k, v ?? ''])));
    setCohorts(policy.cohorts.map((c) => ({ ...c, userIds: [...c.userIds] })));
  }, [policy]);

  const approvedList = approved.split(/\n|,/).map((s) => s.trim()).filter(Boolean);
  const semver = (v: string) => /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/.test(v);
  const policyDirty = policy ? minAppVersion !== policy.minAppVersion || currentAppVersion !== policy.currentAppVersion || channel !== policy.channel || stage !== policy.stage || approvedList.join(',') !== policy.approvedBuildIds.join(',') || JSON.stringify(stageBuildIds) !== JSON.stringify(Object.fromEntries(Object.entries(policy.stageBuildIds).map(([k, v]) => [k, v ?? '']))) : false;
  const cohortsDirty = policy ? JSON.stringify(cohorts.map((c) => ({ id: c.id, userIds: [...c.userIds].sort() }))) !== JSON.stringify(policy.cohorts.map((c) => ({ id: c.id, userIds: [...c.userIds].sort() }))) : false;

  const savePolicy = async () => {
    if (!policy) return;
    await mutation.run('admin.ops.releasePolicy.set', { input: { minAppVersion, currentAppVersion, channel, stage, approvedBuildIds: approvedList, stageBuildIds: Object.fromEntries(Object.entries(stageBuildIds).map(([k, v]) => [k, v || null])) as api.ReleasePolicy['stageBuildIds'], revision: policy.revision } }, { title: 'Release policy updated', invalidate: ['admin.ops.releasePolicy', 'admin.overview'] });
  };
  const saveCohorts = async () => {
    if (!policy) return;
    await mutation.run('admin.ops.cohorts.set', { input: { cohorts, revision: policy.revision } }, { title: 'Cohorts updated', invalidate: ['admin.ops.releasePolicy', 'admin.overview'] });
  };
  const toggleMember = (cohortId: string, userId: api.User['id']) => setCohorts((prev) => prev.map((c) => (c.id === cohortId ? { ...c, userIds: c.userIds.includes(userId) ? c.userIds.filter((u) => u !== userId) : [...c.userIds, userId] } : c)));

  return (
    <Page>
      <PageHeader
        eyebrow="System operations"
        eyebrowPath="Release policy"
        title="Release policy"
        subtitle={policy ? `Policy v${policy.policyVersion} · current ${policy.currentAppVersion} · minimum ${policy.minAppVersion} · ${ROLLOUT_LABEL[policy.stage].toLowerCase()} · ${policy.approvedBuildIds.length} approved builds · updated ${fmtTimestamp(policy.updatedAt)}` : 'Loading release policy.'}
        actions={canEdit ? <SensitiveButton variant="primary" disabled={!policyDirty || !semver(minAppVersion) || !semver(currentAppVersion)} loading={mutation.busy} onClick={() => void savePolicy()}>Save policy</SensitiveButton> : undefined}
      />
      <ErrorCallout error={policyQ.error} onRetry={() => void policyQ.reload()} className="mt-6" />
      {!policy ? (
        <Skeleton height={320} className="mt-6" />
      ) : (
        <Grid>
          <Card title="Desktop versions" subtitle="Desktop updates follow published ToDesktop releases for everyone" span="col-span-12 lg:col-span-5">
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Current version" error={semver(currentAppVersion) ? undefined : 'semver required'}><Input mono value={currentAppVersion} disabled={!canEdit} onChange={(e) => setCurrentAppVersion(e.target.value)} /></Field>
                <Field label="Minimum version" help="Older builds are blocked at entitlement." error={semver(minAppVersion) ? undefined : 'semver required'}><Input mono value={minAppVersion} disabled={!canEdit} onChange={(e) => setMinAppVersion(e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Channel"><Select value={channel} disabled={!canEdit} onValueChange={(v) => setChannel(v as api.ReleaseChannel)} options={api.RELEASE_CHANNELS.map((c) => ({ value: c, label: c }))} /></Field>
                <Field label="Rollout stage"><Select value={stage} disabled={!canEdit} onValueChange={(v) => setStage(v as api.RolloutStage)} options={api.ROLLOUT_STAGES.map((s) => ({ value: s, label: ROLLOUT_LABEL[s] }))} /></Field>
              </div>
              <Field label="Approved build ids" hint={`${approvedList.length}`} help="Legacy policy metadata, one per line. These ids and stages do not gate desktop update delivery; the minimum version is still enforced."><Textarea mono rows={4} value={approved} disabled={!canEdit} onChange={(e) => setApproved(e.target.value)} /></Field>
              <div>
                <p className="label-mono">Build per stage</p>
                <div className="mt-2 grid grid-cols-3 gap-3">
                  {(['cohort_3', 'cohort_10', 'all'] as const).map((s) => (
                    <Field key={s} label={ROLLOUT_LABEL[s]}><Input mono value={stageBuildIds[s] ?? ''} disabled={!canEdit} placeholder="not approved" onChange={(e) => setStageBuildIds((prev) => ({ ...prev, [s]: e.target.value }))} /></Field>
                  ))}
                </div>
              </div>
              <KeyValue items={[{ key: 'Updated by', value: <UserLink userId={policy.updatedByUserId} /> }, { key: 'Revision', value: String(policy.revision), mono: true }]} />
            </div>
          </Card>
          <Card title="Cohorts" subtitle="Named users per stage · 3 → 10 → all" span="col-span-12 lg:col-span-7" rightSlot={canEdit ? <SensitiveButton variant="tertiary" size="sm" disabled={!cohortsDirty} loading={mutation.busy} onClick={() => void saveCohorts()}>Save cohorts</SensitiveButton> : undefined}>
            <Callout tone="info" className="mb-4">Full publication follows cohort approval. IP-based partial releases cannot identify users behind a shared office IP.</Callout>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {cohorts.map((c) => (
                <div key={c.id} className="rounded-sm border border-line bg-bg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-medium">{c.name}</span>
                    <Chip tone={ROLLOUT_TONE[c.id as api.RolloutStage] ?? 'mute'}>{c.userIds.length} users</Chip>
                  </div>
                  <ul className="scrollbar-thin mt-3 max-h-64 space-y-1 overflow-y-auto">
                    {(people.data?.items ?? []).map((u) => (
                      <li key={u.id}>
                        <Checkbox label={u.displayName} checked={c.userIds.includes(u.id)} disabled={!canEdit} onCheckedChange={() => toggleMember(c.id, u.id)} />
                      </li>
                    ))}
                    {(people.data?.items.length ?? 0) === 0 ? <li className="text-xs text-mute">{c.userIds.length} members · person list unavailable</li> : null}
                  </ul>
                </div>
              ))}
            </div>
            {mutation.error && mutation.error.code === 'revision_mismatch' ? <div className="mt-4"><Button variant="tertiary" onClick={() => void policyQ.reload()}>Reload policy</Button></div> : null}
          </Card>
        </Grid>
      )}
    </Page>
  );
}
